/**
 * Whether a workflow's launch surface should render/send the "Oversight Level"
 * selector. Oversight is a v2 (Agent Runtime) concept ONLY — legacy v1 workflows
 * (e.g. carrier_mortgagee_update) have no oversight and must neither show the
 * control nor send `oversight_level` on launch.
 *
 * Single source of truth imported by BOTH front-ends (ui/CLAUDE.md parity rule):
 *   ora:     ~/common/workflows/shouldShowOversight
 *   add-in:  @product-app/common/workflows/shouldShowOversight
 *
 * Detection is schema-driven: a v2 workflow's `input_schema` declares an
 * `oversight_level` property; v1's does not. The `_v2` workflow-id suffix is a
 * fallback for the window where `input_schema` isn't yet populated on the entry.
 *
 * The param is a minimal structural type so both apps' `WorkflowCatalogEntry`
 * shapes satisfy it (add-in: `input_schema?: Record<string, unknown>`;
 * ora: `input_schema: Record<string, unknown> | null`).
 */

export interface OversightCatalogEntryLike {
  id?: string | null;
  input_schema?: Record<string, unknown> | null;
}

export function shouldShowOversight(
  catalogEntry: OversightCatalogEntryLike | null | undefined,
): boolean {
  if (!catalogEntry) return false;

  // Primary: the workflow's input schema declares an `oversight_level` field.
  const schema = catalogEntry.input_schema;
  if (schema && typeof schema === "object") {
    const props = (schema as { properties?: unknown }).properties;
    if (
      props &&
      typeof props === "object" &&
      "oversight_level" in (props as Record<string, unknown>)
    ) {
      return true;
    }
  }

  // Fallback: v2 workflow-id suffix — covers the window where `input_schema`
  // hasn't been populated on the catalog entry at render time.
  const id = catalogEntry.id;
  return typeof id === "string" && id.endsWith("_v2");
}
